'use client';

import { css } from '@repo/pandacss/styled-system/css';
import { ReactNode, startTransition, useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/shallow';

type StickyBarStoreProps<TFilterState extends Record<string, string | string[]>, TSortValue> = <T>(
  selector: (state: {
    handleClearFilters: () => void;
    appliedFilters: TFilterState | null;
    sortBy: TSortValue;
  }) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => T;

type Props<TFilterState extends Record<string, string | string[]>, TSortValue> = {
  showStickyBar: boolean;
  useStore: StickyBarStoreProps<TFilterState, TSortValue>;
  children: ReactNode;
  clearButtonText?: string;
  scrollBackToMainBar: () => void;
  onClearFilters?: () => void;
  clearAllFiltersEvent: () => void;
  initialClientRegion?: string;
};

const barClass = css({
  backgroundColor: 'white',
  boxShadow: '[0px 4px 4px 0px rgba(0,0,0,0.15)]',
  width: 'full',
  paddingBlockStart: '6',
  paddingBlockEnd: '6',
  position: 'fixed',
  top: '0',
  left: '0',
  zIndex: '200',
  display: 'none',
  sm: { display: 'flex' },
});

const innerClass = css({
  paddingLeft: '3.5',
  maxWidth: '[1440px]',
  width: 'full',
  marginInlineStart: 'auto',
  marginInlineEnd: 'auto',
  sm: { paddingRight: '3.5' },
});

const midClass = css({
  maxWidth: { md: '[82%]' },
  marginInlineStart: { md: 'auto' },
  marginInlineEnd: { md: 'auto' },
});

const rowClass = css({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'row',
  marginInlineStart: 'auto',
  marginInlineEnd: 'auto',
  width: 'full',
});

const filtersClass = css({ display: 'flex', color: 'neutral.darker', columnGap: '2', rowGap: '2' });

const clearButtonClass = css({
  fontSize: 'sm',
  fontWeight: 'medium',
  color: 'neutral.dark',
  backgroundColor: 'neutral.lighter2',
  borderTopLeftRadius: 'lg',
  borderTopRightRadius: 'lg',
  borderBottomRightRadius: 'lg',
  borderBottomLeftRadius: 'lg',
  paddingInlineStart: '3',
  paddingInlineEnd: '3',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  cursor: 'pointer',
  fontFamily: '[inherit]',
  alignSelf: 'flex-start',
  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  _disabled: { opacity: '0.4', cursor: 'not-allowed' },
});

export const StickyBar = <TFilterState extends Record<string, string | string[]>, TSortValue>({
  showStickyBar,
  useStore,
  children,
  clearButtonText = 'Clear All',
  scrollBackToMainBar,
  onClearFilters,
  clearAllFiltersEvent,
  initialClientRegion,
}: Props<TFilterState, TSortValue>) => {
  const { handleClearFilters, appliedFilters, sortBy } = useStore(
    useShallow(state => ({
      handleClearFilters: state.handleClearFilters,
      appliedFilters: state.appliedFilters,
      sortBy: state.sortBy,
    })),
  );

  const effectiveOnClearFilters = onClearFilters || handleClearFilters;

  const onClearFiltersHandler = useCallback(() => {
    scrollBackToMainBar();
    setTimeout(() => {
      startTransition(() => {
        effectiveOnClearFilters();
        clearAllFiltersEvent();
      });
    }, 100);
  }, [effectiveOnClearFilters, clearAllFiltersEvent, scrollBackToMainBar]);

  const numberOfAppliedFilters = useMemo(
    () =>
      appliedFilters
        ? Object.values(appliedFilters).reduce((acc: number, currentValue: string | string[]) => {
            if (Array.isArray(currentValue)) return acc + currentValue.length;
            if (currentValue === initialClientRegion) return acc;
            return acc + (currentValue ? 1 : 0);
          }, 0)
        : 0,
    [appliedFilters, initialClientRegion],
  );

  if (!showStickyBar) return null;

  return (
    <div className={barClass}>
      <div className={innerClass}>
        <div className={midClass}>
          <div className={rowClass}>
            <div className={filtersClass}>{children}</div>
            <button
              type="button"
              className={clearButtonClass}
              onClick={onClearFiltersHandler}
              disabled={!numberOfAppliedFilters && !sortBy}
            >
              {clearButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
